import { FeatureSwitch, FeatureSwitchInfo } from '@if/core/types'
import { FinancingConfigDto } from '@src/data/types/FinancingConfigDto'
import { FinancingProgramProvider } from '@src/data/types/FinancingProgramProvider'
import { MechanicalWarrantyProduct } from '@src/data/types/MechanicalWarrantyProduct'
import { ProductsProvider } from '@src/data/types/ProductsProvider'
import { ProvinceConfigs } from '@src/data/types/ProvinceConfigs'
import { getApiClient } from '@src/services/api-client'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Merchant, Territory, ValueListItem } from '../../types'
import { DocumentType } from '../../types/DocumentType'
import { TaskType } from '../../types/TaskType'
import { TranslatedEnum } from '../../types/TranslatedEnum'
import {
  makeCreate,
  makeGetListFromApi,
  makeGetListWithIdsFromApi,
  makeGetSingleFromApi,
  makeUpdate,
} from '../make-api'
import { SearchMerchantDto } from './SearchMerchantDto'
import {
  transformDocumentTypeFromApi,
  transformTaskTypeFromApi,
  transformTranslatedEnumFromApi,
} from './config-transform'

export interface ValueListItemDto {
  value: string
  textFr: string
  textEn: string
  priority?: number
  active: boolean
}

export interface ValueListDto {
  id: string
  sortMethod: 'priority' | 'alpha'
  defaultvalue: string | null
  items: ValueListItemDto[]
  expiresOn: number
}

const transMerchantFromApi = (data: Merchant) => ({
  ...data,
  province: data.province?.toUpperCase() ?? '',
})

const configApi = {
  getMerchants: makeCreate<SearchMerchantDto, Merchant[]>('/Merchants/search', undefined, (data: Merchant) =>
    transMerchantFromApi(data),
  ),
  getMerchantById: makeGetSingleFromApi<Merchant>('/Merchants/{id}', transMerchantFromApi),
  getValueList: makeGetSingleFromApi<ValueListDto>('/Configs/{listId}'),
  getTerritories: makeGetListFromApi<Territory>('/Territories'),
  getEnum: makeGetSingleFromApi<Record<string, Record<string, TranslatedEnum>>>(
    '/Enums',
    transformTranslatedEnumFromApi,
  ),
  getDocumentTypes: makeGetSingleFromApi<Record<number, DocumentType>>('/DocumentType', transformDocumentTypeFromApi),
  getFinancingConfigs: makeGetSingleFromApi<FinancingConfigDto[]>('/FinancingConfigs'),
  getProvinceConfigurations: makeGetSingleFromApi<ProvinceConfigs>('/ProvinceConfig'),
  getTaskTypes: makeGetSingleFromApi<Record<number, TaskType>>('/TaskType', transformTaskTypeFromApi),
  getIFinanceHolidays: makeGetListWithIdsFromApi<Date>('/FinancingCompanyEnum/Companies/iFinance/Holidays/{year}'),
  getProductProviders: makeGetListFromApi<ProductsProvider>('/ProductProvider'),
  getMerchantProviderByType: makeGetListWithIdsFromApi<FinancingProgramProvider>(
    '/Merchants/{merchantId}/Providers/{financingProgramId}',
  ),
  getMerchantProviderProducts: makeGetListWithIdsFromApi<MechanicalWarrantyProduct>(
    '/Merchants/{merchantId}/{financingProgramId}/Providers/{providerId}/Products',
  ),
  getAllFeatureSwitches: makeGetSingleFromApi<FeatureSwitchInfo>('/FeatureSwitch'),
  updateFeatureSwitch: makeUpdate<FeatureSwitch>('/FeatureSwitch/{id}'),
}

/* Section API React query hooks */

const SCOPE = 'config'
const LIST = 'list'

const keysFactory = {
  list: (listId: string) => [{ scope: SCOPE, entity: LIST, id: listId }] as const,
}

function buildValueList(list: ValueListDto | null, lang: string) {
  if (!list) return []

  const inputList: ValueListItemDto[] = [...list.items]
  if (list.sortMethod === 'priority') inputList.sort((x) => x.priority ?? 0)

  const items: ValueListItem[] = inputList.map((x) => {
    return {
      id: x.value,
      text: lang === 'fr' ? x.textFr : x.textEn,
    }
  })
  if (list.sortMethod === 'alpha') items.sort((one, two) => (one.text > two.text ? -1 : 1))
  return items
}

const getValueList = async (configId: string) => {
  const response = await getApiClient().get<ValueListDto>(`/Configs/${configId}`)
  return response.data
}

export function useGetValueList(configId: string): [ValueListItem[] | undefined, boolean] {
  const {
    i18n: { resolvedLanguage },
  } = useTranslation()
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.list(configId),
    queryFn: () => getValueList(configId),
  })

  const formatedData = useMemo<ValueListItem[]>(
    () => (data ? buildValueList(data, resolvedLanguage ?? 'en') : []),
    [data, resolvedLanguage],
  )

  return [formatedData, isFetching]
}

export default configApi
